import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, InputNumber, Select, Button, Input, Card, Table, message, Grid, Row, Col, Switch, Alert } from 'antd';
import { SaveOutlined, RobotOutlined } from '@ant-design/icons';
import AiPanel from './AiPanel';

const { Option } = Select;
const { useBreakpoint } = Grid;

const elementColors = {
  木: '#52c41a', 火: '#f5222d', 土: '#d4a017', 金: '#faad14', 水: '#1890ff'
};

// 交替行底色
const rowBgColors = ['#ffffff', '#fafafa'];
const selectedRowBg = '#e6f7ff';
const currentRowBg = '#fffbe6'; // 当前年份高亮
const DEFAULT_TIMEZONE = 'Asia/Shanghai';
const TIMEZONE_OPTIONS = [
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Taipei',
  'Asia/Tokyo',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Australia/Sydney'
].map(value => ({ value, label: value }));

function getAppDateParts(timeZone = DEFAULT_TIMEZONE) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(new Date());
  const partMap = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return {
    year: Number(partMap.year),
    month: Number(partMap.month),
    day: Number(partMap.day),
    date: `${partMap.year}-${partMap.month}-${partMap.day}`
  };
}

function normalizeBirthInput(input = {}) {
  const calendar = input.calendar || 'gregorian';
  return {
    year: Number(input.year),
    month: Number(input.month),
    day: Number(input.day),
    time: Number(input.time ?? input.hour ?? 0),
    minute: Number(input.minute ?? 0),
    calendar,
    gender: input.gender || 'male',
    timezone: input.timezone || DEFAULT_TIMEZONE,
    sect: Number(input.sect ?? 2),
    leap_month: calendar === 'lunar' && Boolean(input.leap_month)
  };
}

function sameBirthInput(left, right) {
  if (!left || !right) return false;
  return JSON.stringify(normalizeBirthInput(left)) === JSON.stringify(normalizeBirthInput(right));
}

function birthInputParams(input, extra = {}) {
  const normalized = normalizeBirthInput(input);
  return new URLSearchParams({
    ...normalized,
    leap_month: normalized.leap_month ? 'true' : 'false',
    ...extra
  });
}

async function responseData(response, fallbackMessage) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || fallbackMessage);
  return data;
}

function generateStaticMarkdown(baziResult, snapshotInput) {
  let md = '';
  md += '### 基本信息\n';
  md += `- 出生输入: ${snapshotInput.year}年${snapshotInput.month}月${snapshotInput.day}日 ${snapshotInput.time}时${snapshotInput.minute}分（${snapshotInput.calendar === 'lunar' ? '农历' : '公历'}，${snapshotInput.timezone}）\n`;
  md += `- 排盘基准: ${baziResult.solar.year}年${baziResult.solar.month}月${baziResult.solar.day}日 ${baziResult.solar.hour}时${baziResult.solar.minute}分（${baziResult.calculation_timezone}）\n`;
  md += `- 排盘农历: ${baziResult.lunar.year}年${baziResult.lunar.month}月${baziResult.lunar.day}日 ${baziResult.lunar.hour}时${baziResult.lunar.minute}分\n`;
  md += `- 性别: ${snapshotInput.gender === 'male' ? '男' : '女'}\n`;
  md += `- 时区: ${snapshotInput.timezone}\n`;
  md += `- 子时换日: ${snapshotInput.sect === 1 ? '23点换日' : '0点换日'}\n\n`;
  md += '### 四柱\n';
  const cols = ['年柱','月柱','日柱','时柱'];
  md += `| 属性 | ${cols.join(' | ')} |\n`;
  md += `| --- | ${cols.map(()=>'---').join(' | ')} |\n`;
  const rows = [
    { label:'十神', vals: baziResult.pillars.map(p=>p.ten_deity) },
    { label:'干支', vals: baziResult.pillars.map(p=>p.gan+p.zhi) },
    { label:'藏干', vals: baziResult.pillars.map(p => p.hidden_stems.map((h, idx) => `${h}${p.hidden_ten_deities[idx]}`).join(',')) },
    { label:'神煞', vals: baziResult.pillars.map(p=>p.shensha.join(',')) }
  ];
  rows.forEach(r => { md += `| ${r.label} | ${r.vals.join(' | ')} |\n`; });
  md += '\n### 大运\n';
  md += '| 干支 | 起始年龄 | 起始年 | 十神干 | 十神支 | 藏干 | 神煞 |\n';
  md += '| --- | --- | --- | --- | --- | --- | --- |\n';
  baziResult.dayun.forEach(d => {
    md += `| ${d.gan}${d.zhi} | ${d.start_age} | ${d.start_year} | ${d.ten_deity_stem} | ${d.ten_deity_zhi||''} | ${d.hidden_stems.join(',')} | ${d.shensha.join(',')} |\n`;
  });
  return md;
}

function generateDynamicMarkdown(liunianResult, liuyueResult) {
  let md = '';
  if (liunianResult) {
    md += '\n### 流年\n';
    md += '| 年 | 干支 | 年龄（虚岁） | 十神干 | 十神支 | 藏干 | 神煞 |\n';
    md += '| --- | --- | --- | --- | --- | --- | --- |\n';
    liunianResult.forEach(l => {
      md += `| ${l.year} | ${l.gan}${l.zhi} | ${l.age} | ${l.ten_deity_stem} | ${l.ten_deity_zhi||''} | ${l.hidden_stems.join(',')} | ${l.shensha.join(',')} |\n`;
    });
  }
  if (liuyueResult) {
    md += '\n### 流月\n';
    md += '| 月 | 干支 | 节气区间 | 十神干 | 十神支 | 藏干 | 神煞 |\n';
    md += '| --- | --- | --- | --- | --- | --- | --- |\n';
    liuyueResult.forEach(m => {
      md += `| ${m.month_name || m.month} | ${m.gan}${m.zhi} | ${m.start_at || ''} — ${m.end_at || ''} (${m.timezone || ''}) | ${m.ten_deity_stem} | ${m.ten_deity_zhi||''} | ${m.hidden_stems.join(',')} | ${m.shensha.join(',')} |\n`;
    });
  }
  return md;
}

export default function BaziPanel({ username, initialRecord, onRecordSaved }) {
  const [form] = Form.useForm();
  const [baziResult, setBaziResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [liunianResult, setLiunianResult] = useState(null);
  const [liuyueResult, setLiuyueResult] = useState(null);
  const [selectedDayunIndex, setSelectedDayunIndex] = useState(null);
  const [selectedLiunianIndex, setSelectedLiunianIndex] = useState(null);
  const [personName, setPersonName] = useState('');
  const [showAi, setShowAi] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState(initialRecord?.id || null);
  const [savedAnalyses, setSavedAnalyses] = useState(initialRecord?.analyses || []);
  const [chartSnapshot, setChartSnapshot] = useState(null);
  const [dynamicLoading, setDynamicLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const chartSnapshotRef = useRef(null);
  const chartRequestRef = useRef(null);
  const dynamicRequestRef = useRef({ liunian: null, liuyue: null });
  const saveRequestRef = useRef(null);
  const chartGenerationRef = useRef(0);
  const dynamicGenerationRef = useRef(0);

  const abortDynamicRequests = useCallback(() => {
    dynamicRequestRef.current.liunian?.abort();
    dynamicRequestRef.current.liuyue?.abort();
    dynamicRequestRef.current = { liunian: null, liuyue: null };
  }, []);

  const invalidateChart = useCallback(() => {
    chartGenerationRef.current += 1;
    dynamicGenerationRef.current += 1;
    chartRequestRef.current?.abort();
    saveRequestRef.current?.abort();
    saveRequestRef.current = null;
    abortDynamicRequests();
    chartSnapshotRef.current = null;
    setChartSnapshot(null);
    setBaziResult(null);
    setLiunianResult(null);
    setLiuyueResult(null);
    setSelectedDayunIndex(null);
    setSelectedLiunianIndex(null);
    setCurrentRecordId(null);
    setSavedAnalyses([]);
    setShowAi(false);
    setSaveError('');
    setLoading(false);
    setDynamicLoading(false);
  }, [abortDynamicRequests]);

  // 如果有 initialRecord，自动加载
  useEffect(() => {
    if (initialRecord) {
      form.setFieldsValue({
        year: initialRecord.year, month: initialRecord.month, day: initialRecord.day,
        time: initialRecord.time ?? initialRecord.hour ?? 0,
        minute: initialRecord.minute ?? 0,
        calendar: initialRecord.calendar || 'gregorian',
        gender: initialRecord.gender || 'male',
        timezone: initialRecord.timezone || DEFAULT_TIMEZONE,
        sect: initialRecord.sect ?? 2,
        leap_month: Boolean(initialRecord.leap_month)
      });
      setPersonName(initialRecord.name || '');
      setCurrentRecordId(initialRecord.id);
      setSavedAnalyses(initialRecord.analyses || []);
      // 自动提交
      form.submit();
    }
  }, [form, initialRecord]);

  useEffect(() => () => {
    chartRequestRef.current?.abort();
    saveRequestRef.current?.abort();
    abortDynamicRequests();
  }, [abortDynamicRequests]);

  const saveSnapshotRecord = async (snapshot, chartGeneration) => {
    if (initialRecord && sameBirthInput(initialRecord.input || initialRecord, snapshot.input)) {
      if (
        chartGenerationRef.current !== chartGeneration ||
        chartSnapshotRef.current?.chart_id !== snapshot.chart_id
      ) return null;
      setCurrentRecordId(initialRecord.id);
      setPersonName(initialRecord.name || '');
      setSavedAnalyses(initialRecord.analyses || []);
      return initialRecord.id;
    }

    saveRequestRef.current?.abort();
    const controller = new AbortController();
    saveRequestRef.current = controller;
    const defaultName = `${snapshot.input.year}-${snapshot.input.month}-${snapshot.input.day} ${snapshot.input.gender === 'male' ? '男' : '女'}`;
    try {
      const res = await fetch('/api/user/records', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          name: defaultName,
          ...snapshot.input,
          hour: snapshot.input.time,
          input: snapshot.input,
          chart_id: snapshot.chart_id,
          calculation_version: snapshot.calculation_version
        }),
        signal: controller.signal
      });
      const data = await responseData(res, '保存排盘记录失败');
      if (!data.record?.id) throw new Error('保存排盘记录失败：响应缺少记录编号');
      if (
        chartGenerationRef.current !== chartGeneration ||
        chartSnapshotRef.current?.chart_id !== snapshot.chart_id ||
        saveRequestRef.current !== controller
      ) return null;
      setCurrentRecordId(data.record.id);
      setPersonName(defaultName);
      setSavedAnalyses([]);
      onRecordSaved?.();
      return data.record.id;
    } finally {
      if (saveRequestRef.current === controller) saveRequestRef.current = null;
    }
  };

  const fetchLiuyueForSnapshot = async (snapshot, dayunIndex, liunianIndex, generation) => {
    dynamicRequestRef.current.liuyue?.abort();
    const controller = new AbortController();
    dynamicRequestRef.current.liuyue = controller;
    const params = birthInputParams(snapshot.input, {
      dayun_index: dayunIndex,
      liunian_index: liunianIndex
    });
    const res = await fetch(`/api/bazi/liuyue?${params}`, { signal: controller.signal });
    const data = await responseData(res, '加载流月失败');
    if (
      dynamicGenerationRef.current !== generation ||
      chartSnapshotRef.current?.chart_id !== snapshot.chart_id ||
      data.chart_id !== snapshot.chart_id
    ) return false;
    setLiuyueResult(data.liuyue || []);
    return true;
  };

  const fetchLiunianForSnapshot = async (snapshot, dayunIndex, selectCurrent, generation) => {
    dynamicRequestRef.current.liunian?.abort();
    dynamicRequestRef.current.liuyue?.abort();
    const controller = new AbortController();
    dynamicRequestRef.current = { liunian: controller, liuyue: null };
    const params = birthInputParams(snapshot.input, { dayun_index: dayunIndex });
    const res = await fetch(`/api/bazi/liunian?${params}`, { signal: controller.signal });
    const data = await responseData(res, '加载流年失败');
    if (
      dynamicGenerationRef.current !== generation ||
      chartSnapshotRef.current?.chart_id !== snapshot.chart_id ||
      data.chart_id !== snapshot.chart_id
    ) return false;

    const liunian = data.liunian || [];
    setLiunianResult(liunian);
    if (!selectCurrent) return true;

    const currentLiunianIdx = liunian.findIndex(item => item.is_current);
    if (currentLiunianIdx === -1) return true;
    setSelectedLiunianIndex(currentLiunianIdx);
    return fetchLiuyueForSnapshot(snapshot, dayunIndex, currentLiunianIdx, generation);
  };

  const handleSubmit = async (values) => {
    invalidateChart();
    const chartGeneration = chartGenerationRef.current;
    const controller = new AbortController();
    chartRequestRef.current = controller;
    setLoading(true);
    try {
      const res = await fetch(`/api/bazi?${birthInputParams(values)}`, { signal: controller.signal });
      const data = await responseData(res, '排盘失败');
      if (chartGenerationRef.current !== chartGeneration) return;
      if (!data.input || !data.chart_id) throw new Error('排盘响应缺少规范化输入或命盘编号');

      const snapshot = Object.freeze({
        input: Object.freeze(normalizeBirthInput(data.input)),
        chart_id: data.chart_id,
        calculation_version: data.calculation_version,
        age_convention: data.age_convention
      });
      chartSnapshotRef.current = snapshot;
      setChartSnapshot(snapshot);
      setBaziResult(data);

      try {
        const recordId = await saveSnapshotRecord(snapshot, chartGeneration);
        if (
          !recordId ||
          chartGenerationRef.current !== chartGeneration ||
          chartSnapshotRef.current?.chart_id !== snapshot.chart_id
        ) return;
      } catch (error) {
        if (chartGenerationRef.current !== chartGeneration) return;
        setSaveError(error.message || '保存排盘记录失败');
        message.error(error.message || '保存排盘记录失败');
        return;
      }

      const currentYear = getAppDateParts(snapshot.input.timezone).year;
      const dayunList = data.dayun || [];
      const currentDayunIdx = dayunList.findIndex((item, index) => {
        const endYear = dayunList[index + 1]?.start_year ?? item.start_year + 10;
        return currentYear >= item.start_year && currentYear < endYear;
      });
      if (currentDayunIdx !== -1) {
        const dynamicGeneration = dynamicGenerationRef.current + 1;
        dynamicGenerationRef.current = dynamicGeneration;
        setDynamicLoading(true);
        setSelectedDayunIndex(currentDayunIdx);
        try {
          await fetchLiunianForSnapshot(snapshot, currentDayunIdx, true, dynamicGeneration);
        } catch (error) {
          if (error.name !== 'AbortError' && dynamicGenerationRef.current === dynamicGeneration) {
            message.error(error.message || '加载当前岁运失败');
          }
        } finally {
          if (dynamicGenerationRef.current === dynamicGeneration) setDynamicLoading(false);
        }
      }
      if (chartGenerationRef.current === chartGeneration && chartSnapshotRef.current?.chart_id === snapshot.chart_id) {
        setShowAi(true);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && chartGenerationRef.current === chartGeneration) {
        message.error(error.message || '排盘失败');
      }
    } finally {
      if (chartGenerationRef.current === chartGeneration) setLoading(false);
    }
  };

  const fetchLiunian = async (index) => {
    const snapshot = chartSnapshotRef.current;
    if (!snapshot) return;
    const generation = dynamicGenerationRef.current + 1;
    dynamicGenerationRef.current = generation;
    abortDynamicRequests();
    setDynamicLoading(true);
    setSelectedDayunIndex(index);
    setLiunianResult(null);
    setLiuyueResult(null);
    setSelectedLiunianIndex(null);
    try {
      await fetchLiunianForSnapshot(snapshot, index, false, generation);
    } catch (error) {
      if (error.name !== 'AbortError' && dynamicGenerationRef.current === generation) {
        message.error(error.message || '加载流年失败');
      }
    } finally {
      if (dynamicGenerationRef.current === generation) setDynamicLoading(false);
    }
  };

  const fetchLiuyue = async (liunianIndex) => {
    const snapshot = chartSnapshotRef.current;
    if (!snapshot || selectedDayunIndex == null) return;
    const generation = dynamicGenerationRef.current + 1;
    dynamicGenerationRef.current = generation;
    dynamicRequestRef.current.liuyue?.abort();
    setDynamicLoading(true);
    setSelectedLiunianIndex(liunianIndex);
    setLiuyueResult(null);
    try {
      await fetchLiuyueForSnapshot(snapshot, selectedDayunIndex, liunianIndex, generation);
    } catch (error) {
      if (error.name !== 'AbortError' && dynamicGenerationRef.current === generation) {
        message.error(error.message || '加载流月失败');
      }
    } finally {
      if (dynamicGenerationRef.current === generation) setDynamicLoading(false);
    }
  };

  const renameRecord = async () => {
    if (!personName.trim() || !currentRecordId) return;
    try {
      const res = await fetch(`/api/user/records/${currentRecordId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, name: personName.trim() })
      });
      if (!res.ok) throw new Error((await res.json()).error);
      message.success('已更名');
      if (onRecordSaved) onRecordSaved();
    } catch (e) {
      message.error(e.message);
    }
  };

  const buildAnalysisPayload = useCallback(() => {
    if (!baziResult || !chartSnapshot) return { markdown: '', analysisContext: null };
    const staticMarkdown = generateStaticMarkdown(baziResult, chartSnapshot.input);
    const dynamicMarkdown = generateDynamicMarkdown(liunianResult, liuyueResult);
    const appDate = getAppDateParts(chartSnapshot.input.timezone);
    const timezone = chartSnapshot.input.timezone;
    const asOfDate = appDate.date;
    return {
      markdown: `${staticMarkdown}${dynamicMarkdown}`,
      analysisContext: {
        static_markdown: staticMarkdown,
        dynamic_markdown: dynamicMarkdown,
        chart_payload: {
          input: chartSnapshot.input,
          chart_id: chartSnapshot.chart_id,
          calculation_version: chartSnapshot.calculation_version,
          calculation_timezone: baziResult.calculation_timezone,
          age_convention: chartSnapshot.age_convention,
          solar: baziResult.solar,
          lunar: baziResult.lunar,
          pillars: baziResult.pillars,
          dayun: baziResult.dayun
        },
        dynamic_payload: {
          as_of_date: asOfDate,
          timezone,
          focus_scope: 'default',
          selected_dayun_index: selectedDayunIndex,
          selected_liunian_index: selectedLiunianIndex,
          selected_dayun: selectedDayunIndex != null ? baziResult.dayun[selectedDayunIndex] : null,
          selected_liunian: selectedLiunianIndex != null && liunianResult ? liunianResult[selectedLiunianIndex] : null,
          liuyue: liuyueResult || []
        }
      }
    };
  }, [baziResult, chartSnapshot, liunianResult, liuyueResult, selectedDayunIndex, selectedLiunianIndex]);

  const pillarColumns = [
    { title: '属性', dataIndex: 'label', key: 'label', width: 80 },
    ...['年柱','月柱','日柱','时柱'].map((col, i) => ({
      title: col, dataIndex: `col${i}`, key: col, align: 'center',
      render: (val) => val
    }))
  ];

  const getPillarData = () => {
    if (!baziResult) return [];
    const p = baziResult.pillars;
    return [
      { key: '1', label: '十神', col0: p[0].ten_deity, col1: p[1].ten_deity, col2: p[2].ten_deity, col3: p[3].ten_deity },
      { key: '2', label: '天干', col0: <span style={{color: elementColors[p[0].wuxing_stem], fontWeight:'bold', fontSize:18}}>{p[0].gan}</span>,
        col1: <span style={{color: elementColors[p[1].wuxing_stem], fontWeight:'bold', fontSize:18}}>{p[1].gan}</span>,
        col2: <span style={{color: elementColors[p[2].wuxing_stem], fontWeight:'bold', fontSize:18}}>{p[2].gan}</span>,
        col3: <span style={{color: elementColors[p[3].wuxing_stem], fontWeight:'bold', fontSize:18}}>{p[3].gan}</span> },
      { key: '3', label: '地支', col0: <span style={{color: elementColors[p[0].wuxing_branch], fontWeight:'bold', fontSize:18}}>{p[0].zhi}</span>,
        col1: <span style={{color: elementColors[p[1].wuxing_branch], fontWeight:'bold', fontSize:18}}>{p[1].zhi}</span>,
        col2: <span style={{color: elementColors[p[2].wuxing_branch], fontWeight:'bold', fontSize:18}}>{p[2].zhi}</span>,
        col3: <span style={{color: elementColors[p[3].wuxing_branch], fontWeight:'bold', fontSize:18}}>{p[3].zhi}</span> },
      { key: '4', label: '藏干', col0: p[0].hidden_stems.map((h,i)=>`${h}(${p[0].hidden_ten_deities[i]})`).join(' '),
        col1: p[1].hidden_stems.map((h,i)=>`${h}(${p[1].hidden_ten_deities[i]})`).join(' '),
        col2: p[2].hidden_stems.map((h,i)=>`${h}(${p[2].hidden_ten_deities[i]})`).join(' '),
        col3: p[3].hidden_stems.map((h,i)=>`${h}(${p[3].hidden_ten_deities[i]})`).join(' ') },
      { key: '5', label: '神煞', col0: p[0].shensha.join(', '), col1: p[1].shensha.join(', '), col2: p[2].shensha.join(', '), col3: p[3].shensha.join(', ') },
    ];
  };

  const dayunColumns = [
    { title: '干支', dataIndex: 'ganzhi', key: 'ganzhi' },
    { title: '起运年龄（虚岁）', dataIndex: 'start_age', key: 'start_age' },
    { title: '起运年', dataIndex: 'start_year', key: 'start_year' },
    { title: '十神(干)', dataIndex: 'ten_deity_stem', key: 'ten_deity_stem' },
    { title: '十神(支)', dataIndex: 'ten_deity_zhi', key: 'ten_deity_zhi' },
    { title: '藏干', dataIndex: 'hidden', key: 'hidden' },
    { title: '神煞', dataIndex: 'shensha', key: 'shensha' },
  ];

  const liunianColumns = [
    { title: '年', dataIndex: 'year', key: 'year' },
    { title: '干支', dataIndex: 'ganzhi', key: 'ganzhi' },
    { title: '年龄（虚岁）', dataIndex: 'age', key: 'age' },
    { title: '十神(干)', dataIndex: 'ten_deity_stem', key: 'ten_deity_stem' },
    { title: '十神(支)', dataIndex: 'ten_deity_zhi', key: 'ten_deity_zhi' },
    { title: '藏干', dataIndex: 'hidden', key: 'hidden' },
    { title: '神煞', dataIndex: 'shensha', key: 'shensha' },
  ];

  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const calendar = Form.useWatch('calendar', form);
  const appDate = getAppDateParts(chartSnapshot?.input.timezone);
  const analysisPayload = buildAnalysisPayload();

  return (
    <div style={{ padding: isMobile ? 12 : 24 }}>
      {/* 排盘表单 */}
      <Card title="八字排盘" style={{ marginBottom: 16 }} styles={isMobile ? { body: { padding: 12 } } : undefined}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={invalidateChart}
          initialValues={{
            year: 1985,
            month: 1,
            day: 1,
            time: 0,
            minute: 0,
            calendar: 'gregorian',
            gender: 'male',
            timezone: DEFAULT_TIMEZONE,
            sect: 2,
            leap_month: false
          }}
        >
          <Row gutter={[12, 0]}>
            <Col xs={12} sm={6} md={4} lg={3}>
              <Form.Item name="year" label="年" rules={[{required:true}]}>
                <InputNumber min={1900} max={2100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Item name="month" label="月" rules={[{required:true}]}>
                <InputNumber min={1} max={12} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Item name="day" label="日" rules={[{required:true}]}>
                <InputNumber min={1} max={31} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Item name="time" label="时">
                <InputNumber min={0} max={23} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Item name="minute" label="分">
                <InputNumber min={0} max={59} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Item name="calendar" label="历法">
                <Select>
                  <Option value="gregorian">阳历</Option>
                  <Option value="lunar">阴历</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Item name="gender" label="性别">
                <Select>
                  <Option value="male">男</Option>
                  <Option value="female">女</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={7} lg={4}>
              <Form.Item name="timezone" label="IANA 时区" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={TIMEZONE_OPTIONS}
                  placeholder="搜索时区"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Form.Item name="sect" label="子时换日">
                <Select>
                  <Option value={1}>23点换日</Option>
                  <Option value={2}>0点换日</Option>
                </Select>
              </Form.Item>
            </Col>
            {calendar === 'lunar' && (
              <Col xs={12} sm={6} md={3} lg={2}>
                <Form.Item name="leap_month" label="闰月" valuePropName="checked">
                  <Switch checkedChildren="是" unCheckedChildren="否" />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={12} md={5} lg={3}>
              <Form.Item label=" ">
                <Button type="primary" htmlType="submit" loading={loading} block={isMobile}>排盘</Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
        {saveError && (
          <Alert
            type="error"
            showIcon
            message="排盘已生成，但记录保存失败"
            description={`${saveError}。为避免分析写入错误人物，AI 分析已禁用，请重新排盘后再试。`}
            style={{ marginTop: 12 }}
          />
        )}
        {baziResult && (
          <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Input placeholder="排盘对象姓名（可修改）" value={personName} onChange={e => setPersonName(e.target.value)}
              onPressEnter={renameRecord} style={{ width: isMobile ? '100%' : 200 }} />
            <Button icon={<SaveOutlined />} onClick={renameRecord}>更名</Button>
          </div>
        )}
      </Card>

      {/* 四柱结果 */}
      {baziResult && (
        <>
          <Card title={isMobile
            ? `四柱 — ${baziResult.solar.year}年${baziResult.solar.month}月${baziResult.solar.day}日`
            : `四柱 — 排盘基准 ${baziResult.solar.year}年${baziResult.solar.month}月${baziResult.solar.day}日 ${baziResult.solar.hour}:${String(baziResult.solar.minute).padStart(2, '0')} (${baziResult.calculation_timezone}) | 农历 ${baziResult.lunar.year}年${baziResult.lunar.month}月${baziResult.lunar.day}日`}
            size="small" style={{ marginBottom: 16 }}>
            <Table columns={pillarColumns} dataSource={getPillarData()} pagination={false} bordered size="small"
              scroll={isMobile ? { x: 400 } : undefined} />
          </Card>

          <Card title="大运 (点击查看流年)" size="small" style={{ marginBottom: 16 }}>
            <Table columns={dayunColumns} pagination={false} size="small" loading={dynamicLoading}
              scroll={isMobile ? { x: 500 } : undefined}
              rowClassName={(record, i) => {
                if (i === selectedDayunIndex) return 'row-selected';
                return i % 2 === 0 ? 'row-even' : 'row-odd';
              }}
              onRow={(record, i) => ({
                onClick: () => fetchLiunian(i),
                style: {
                  cursor: 'pointer',
                  ...(i === selectedDayunIndex ? { background: selectedRowBg } :
                    (() => {
                      const dayunList = baziResult.dayun;
                      const startYear = dayunList[i].start_year;
                      const endYear = i + 1 < dayunList.length ? dayunList[i + 1].start_year : startYear + 10;
                      return appDate.year >= startYear && appDate.year < endYear ? { background: currentRowBg } : { background: rowBgColors[i % 2] };
                    })())
                }
              })}
              dataSource={baziResult.dayun.map((d, i) => ({
                key: i, ganzhi: `${d.gan}${d.zhi}`, start_age: d.start_age, start_year: d.start_year,
                ten_deity_stem: d.ten_deity_stem, ten_deity_zhi: d.ten_deity_zhi || '-',
                hidden: d.hidden_stems.join(','), shensha: d.shensha.join(',')
              }))} />
          </Card>
        </>
      )}

      {/* 流年 */}
      {liunianResult && (
        <Card title="流年 (点击查看流月)" size="small" style={{ marginBottom: 16 }}>
          <Table columns={liunianColumns} pagination={false} size="small" loading={dynamicLoading}
            scroll={isMobile ? { x: 500 } : undefined}
            onRow={(record, i) => ({
              onClick: () => fetchLiuyue(i),
              style: {
                cursor: 'pointer',
                ...(i === selectedLiunianIndex ? { background: selectedRowBg } :
                  record.is_current ? { background: currentRowBg } : { background: rowBgColors[i % 2] })
              }
            })}
            dataSource={liunianResult.map((l, i) => ({
              key: i, year: l.year, ganzhi: `${l.gan}${l.zhi}`, age: l.age,
              is_current: Boolean(l.is_current),
              ten_deity_stem: l.ten_deity_stem, ten_deity_zhi: l.ten_deity_zhi || '-',
              hidden: l.hidden_stems.join(','), shensha: l.shensha.join(',')
            }))} />
        </Card>
      )}

      {/* 流月 */}
      {liuyueResult && (
        <Card title="流月" size="small" style={{ marginBottom: 16 }}>
          <Table columns={[
            { title: '月', dataIndex: 'month_name', key: 'month_name' },
            { title: '干支', dataIndex: 'ganzhi', key: 'ganzhi' },
            { title: '节气起止区间', dataIndex: 'solar_term_range', key: 'solar_term_range', width: 320 },
            { title: '十神(干)', dataIndex: 'ten_deity_stem', key: 'ten_deity_stem' },
            { title: '十神(支)', dataIndex: 'ten_deity_zhi', key: 'ten_deity_zhi' },
            { title: '藏干', dataIndex: 'hidden', key: 'hidden' },
            { title: '神煞', dataIndex: 'shensha', key: 'shensha' },
          ]} pagination={false} size="small"
            scroll={isMobile ? { x: 450 } : undefined}
            loading={dynamicLoading}
            onRow={(record, i) => ({
              style: {
                ...(record.is_current ? { background: currentRowBg } : { background: rowBgColors[i % 2] })
              }
            })}
            dataSource={liuyueResult.map((m, i) => ({
              key: i,
              month_name: m.month_name || m.month,
              ganzhi: `${m.gan}${m.zhi}`,
              solar_term_range: `${m.start_at || '-'} — ${m.end_at || '-'} (${m.timezone || chartSnapshot.input.timezone})`,
              is_current: Boolean(m.is_current),
              ten_deity_stem: m.ten_deity_stem, ten_deity_zhi: m.ten_deity_zhi || '-',
              hidden: m.hidden_stems.join(','), shensha: m.shensha.join(',')
            }))} />
        </Card>
      )}

      {/* AI 分析面板 */}
      {showAi && currentRecordId && !saveError && (
        <AiPanel
          markdown={analysisPayload.markdown}
          analysisContext={analysisPayload.analysisContext}
          onClose={() => setShowAi(false)}
          recordId={currentRecordId}
          username={username}
          savedAnalyses={savedAnalyses}
          disabled={loading || dynamicLoading}
        />
      )}
      {!showAi && chartSnapshot && currentRecordId && !saveError && (
        <Button
          type="primary"
          icon={<RobotOutlined />}
          onClick={() => setShowAi(true)}
          disabled={loading || dynamicLoading}
        >
          重新打开 AI 分析
        </Button>
      )}
    </div>
  );
}
